<template>
	<div class="clip-list d-flex flex-column h-100">
		<!-- Upper area: Clips (header + Preview button + list) -->
		<div class="clip-list-body d-flex flex-column flex-grow-1">
			<div class="d-flex justify-space-between align-center mb-3 flex-shrink-0">
				<h6 class="text-h6">Clips</h6>
				<span class="text-body-2 text-medium-emphasis">Total: {{ formatDurationShort(totalDuration) }}</span>
			</div>

			<v-btn
				class="preview-all-clips-btn"
				variant="outlined"
				color="primary"
				rounded="xl"
				@click="previewMode ? emit('stop-preview') : emit('preview-all')">
				<v-icon class="mr-2">{{ previewMode ? "icon-pause" : "icon-play-solid" }}</v-icon>
				{{ previewMode ? "Stop Preview" : "Preview Final Video" }}
			</v-btn>

			<v-divider class="my-3" />

			<div class="d-flex align-center justify-center ga-2 mb-3 clip-merge-info">
				<v-icon size="small" color="info">icon-info-circle-stroke</v-icon>
				<span class="text-caption text-info">All pieces will be combined into a single video</span>
			</div>

			<v-list class="clip-list-scroller flex-grow-1 pa-0 bg-transparent overflow-y-auto">
				<ClipListItem
					v-for="(clip, index) in clips"
					:key="clip.id"
					:clip="clip"
					:index="index"
					:is-active="clip.id === activeClipId"
					class="mb-2"
					@remove="emit('remove-clip', clip.id)"
					@click="emit('clip-click', clip.id)" />
			</v-list>
		</div>

		<!-- Bottom area: Tips + Keyboard Shortcuts -->
		<v-divider class="mt-4" />
		<div class="flex-shrink-0 pt-4">
			<div class="text-subtitle-2 text-medium-emphasis mb-2">How to edit?</div>
			<ol class="pl-6 mb-3">
				<li class="text-caption text-medium-emphasis mb-1">
					Move the blue marker left or right along the timeline to find where you want to start.
				</li>
				<li class="text-caption text-medium-emphasis mb-1">
					Click and move left or right, then let go to choose a piece of video on timeline.
				</li>
				<li class="text-caption text-medium-emphasis mb-1">Repeat to pick more pieces.</li>
				<li class="text-caption text-medium-emphasis mb-1">Click trash button to remove a piece.</li>
				<li class="text-caption text-medium-emphasis">
					When you are done editing, click Save. All the pieces will be put in order to make one final video.
				</li>
			</ol>

			<template v-if="!smAndDown">
				<div class="text-subtitle-2 text-medium-emphasis mb-2">Keyboard Shortcuts</div>
				<div class="d-flex align-center ga-2 mb-1">
					<v-chip size="small" variant="outlined">Space</v-chip>
					<span class="text-caption text-medium-emphasis">Play / Pause</span>
				</div>
				<div class="d-flex align-center ga-2 mb-1">
					<v-chip size="small" variant="outlined">Esc</v-chip>
					<span class="text-caption text-medium-emphasis">Clear all Segments</span>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { useDisplay } from "vuetify";
	import ClipListItem from "./ClipListItem.vue";
	import { formatDurationShort } from "@/helpers/time";
	import type { VideoClip } from "@/types/clipVideo";

	const { smAndDown } = useDisplay();

	defineProps<{
		clips: VideoClip[];
		totalDuration: number;
		previewMode: boolean;
		activeClipId: string | null;
	}>();

	const emit = defineEmits<{
		(e: "remove-clip", id: string): void;
		(e: "preview-all"): void;
		(e: "stop-preview"): void;
		(e: "clip-click", id: string): void;
	}>();
</script>

<style scoped>
	/*
	 * LSP-19249: the non-`auto` `min-height` values below are load-bearing, not
	 * decoration.
	 *
	 * A flex item defaults to `min-height: auto`, which refuses to shrink below its
	 * own content. Without an override this column grows to fit every clip, so the
	 * list's `overflow-y: auto` never has a constrained height to scroll inside and
	 * the surplus rows are silently clipped by the panel's `overflow: hidden` in
	 * ClipVideoDialog — clips cut off at the bottom with no scrollbar anywhere.
	 * Vuetify ships no `min-h-*` utility class (its sizing utilities cover
	 * `height`/`width` only), so this cannot live in the template next to the
	 * `h-100` / `flex-grow-1` classes.
	 *
	 * The floor is on the body rather than the scroller because the tips block
	 * below is `flex-shrink-0`: flooring only the scroller lets the body be shrunk
	 * underneath it, and the list then paints over the tips. 260px is the body's
	 * own chrome (heading, Preview button, dividers, merge notice) plus roughly two
	 * visible clips.
	 */
	.clip-list-body {
		min-height: 260px;
	}

	.clip-list-scroller {
		min-height: 0;
	}

	/*
	 * Fallback for panels too short to fit the floored body plus the tips block:
	 * the whole Clips column scrolls rather than clipping its contents.
	 */
	.clip-list {
		overflow-y: auto;
	}
</style>
